import z from 'zod';
const noExceedanceDeclarationSchema = z.looseObject({
    /**
     * What the letter states about exceedances of the engine thrust rating or operating limits:
     * `declared_absent` (no exceedance occurred, the common case), `present` (the letter states or
     * lists one), `deferred` (it points elsewhere for the answer), `unclear` (it does not settle it).
     */
    finding: z.enum(['declared_absent', 'present', 'deferred', 'unclear']),
    /** Verbatim sentence the finding was read from — the grounding evidence, never empty. */
    quote: z.string(),
    /** Every source page the declaration was seen on. */
    pageIds: z.array(z.string()),
});
export const noExceedanceEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('no-exceedance-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Same envelope as `pma-der-events-ready`.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    /**
     * EVERY non-exceedance statement of the project, not a single winner (DR-6373): an operator
     * letter confirming the engine operated within its approved thrust rating and operating limits
     * asserts that for one period, so a project holds several. Read from `No Exceedance Statement`,
     * `NIS Statement` and `PMA/DER Status` letters. A NIS letter contributes one item per stay, like
     * `pma-der-events-ready`. `declarations` is always present and never empty: an item with no
     * declaration is not sent.
     */
    noExceedanceEvents: z.array(z.looseObject({
        documentId: z.string(),
        reconstructedDocumentId: z.string(),
        pageIds: z.array(z.string()),
        statementDate: z.string().optional(),
        /** First/last day of the period the statement covers, YYYY-MM-DD. Absent when no window is known. */
        coveragePeriodStart: z.string().optional(),
        coveragePeriodEnd: z.string().optional(),
        /**
         * The covered period starts when the engine was new ('from New', 'since new', 'since
         * manufacture'); `coveragePeriodStart` is then absent. Only ever `true` or absent.
         */
        coverageSinceNew: z.boolean().optional(),
        /** Company that signed the statement. Absent when pages disagree or none printed a signer. */
        issuerName: z.string().optional(),
        /** `operator` or `camo_on_behalf`. Absent for a shop/lessor/unclear signer, and when pages disagree. */
        issuerRole: z.enum(['operator', 'camo_on_behalf']).optional(),
        /** Engine serials the letter stated, as printed. Brain always emits the array, empty when none. */
        esns: z.array(z.string()),
        // As on `pma-der-events-ready` (DR-6009): `aircraft` means the window is the AIRCRAFT's period
        // and the item counts for an engine only for its stays on that aircraft; absent means engine.
        statementSubject: z.enum(['aircraft', 'engine']).optional(),
        /** Registration of the aircraft the letter names, verbatim. */
        aircraftRegistration: z.string().optional(),
        /** Manufacturer serial number of the aircraft the letter names, verbatim. */
        aircraftMsn: z.string().optional(),
        declarations: z.array(noExceedanceDeclarationSchema),
    })),
});
//# sourceMappingURL=no-exceedance-events-ready.webhook.js.map